import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputOtp } from 'primereact/inputotp';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../hooks/queries/useReportService';
import ConsentCheckbox from './input/ConsentCheckbox';
import ReportBanner from './ReportBanner';

// Page-level composition for the standard "submit single form → render output"
// pattern that ~50 Borrower Report pages share. Wraps:
//   - <ReportBanner> (negotiated banner exception)
//   - <Card> with form (IconField inputs, Dropdown, InputTextarea, P5 inline validation)
//   - <ConfirmDialog> with field summary + credit summary
//   - <Toast> for success / warn / error feedback
//   - Output component rendered after success
// Pages that don't fit (file upload, OTP flow, multi-step wizard, bureau-grade
// outputs) write their own component and skip this helper.

const buildInitial = (fields, initialValues = {}) => {
    const init = { consent: false };
    fields.forEach((f) => {
        // Per-field default priority:
        //   1. value passed by the host via `initialValues` (e.g. payslip
        //      OCR populated formData.uan, host now wants the UAN sidebar
        //      to open with that value pre-filled)
        //   2. field.default declared in the input component
        //   3. type-appropriate empty (false for checkbox, '' otherwise)
        const fromHost = initialValues?.[f.name];
        if (f.type === 'checkbox') {
            init[f.name] = typeof fromHost === 'boolean' ? fromHost : (f.default ?? false);
        } else {
            init[f.name] = (fromHost != null && fromHost !== '') ? fromHost : (f.default ?? '');
        }
    });
    return init;
};

const applyTransform = (field, raw) => {
    let v = raw ?? '';
    if (typeof v === 'string') {
        if (field.transform) v = field.transform(v);
        else if (field.uppercase) v = v.toUpperCase().trim();
        else if (field.lowercase) v = v.toLowerCase().trim();
        else if (field.numeric) v = String(v).replace(/\D/g, '');
        if (field.maxLength && typeof v === 'string') v = v.slice(0, field.maxLength);
    }
    return v;
};

const renderField = (field, formData, errors, setFormData, setErrors, loading) => {
    const handleChange = (e) => {
        const raw = e.target ? e.target.value : e.value;
        const v = field.type === 'checkbox' ? e.checked : applyTransform(field, raw);
        setFormData((prev) => ({ ...prev, [field.name]: v }));
        if (errors[field.name]) setErrors((prev) => ({ ...prev, [field.name]: '' }));
    };

    const value = formData[field.name];
    const err = errors[field.name];
    const helpId = `${field.name}-help`;
    const baseClass = classNames('w-full p-inputtext-sm', field.mono && 'font-mono', { 'p-invalid': !!err });
    const colClass = field.colClass || 'col-12 md:col-6 lg:col-4';

    return (
        <div key={field.name} className={`field ${colClass}`}>
            <label htmlFor={field.name} className="block font-medium text-sm mb-2">
                {field.label}{field.required !== false && <span className="text-red-500"> *</span>}
            </label>

            {field.type === 'dropdown' ? (
                <Dropdown
                    id={field.name}
                    value={value}
                    options={field.options}
                    optionLabel={field.optionLabel || 'label'}
                    optionValue={field.optionValue || 'value'}
                    onChange={handleChange}
                    placeholder={field.placeholder || 'Select'}
                    filter={field.filter}
                    showClear={field.showClear}
                    disabled={loading}
                    className={baseClass}
                    aria-describedby={helpId}
                    aria-invalid={!!err}
                />
            ) : field.type === 'textarea' ? (
                <InputTextarea
                    id={field.name}
                    value={value}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    maxLength={field.maxLength}
                    rows={field.rows || 1}
                    autoResize
                    disabled={loading}
                    className={baseClass}
                    aria-describedby={helpId}
                    aria-invalid={!!err}
                />
            ) : (
                <IconField iconPosition="left" className="w-full">
                    {field.icon && <InputIcon className={`pi ${field.icon}`} />}
                    <InputText
                        id={field.name}
                        value={value}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        maxLength={field.maxLength}
                        type={field.htmlType || 'text'}
                        inputMode={field.inputMode}
                        autoCapitalize={field.autoCapitalize}
                        autoComplete={field.autoComplete}
                        spellCheck={field.spellCheck === false ? false : undefined}
                        disabled={loading}
                        className={baseClass}
                        aria-describedby={helpId}
                        aria-invalid={!!err}
                    />
                </IconField>
            )}

            {err && (
                <small id={helpId} className="p-error block mt-1" role="alert">
                    <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                    {err}
                </small>
            )}
        </div>
    );
};

const SimpleReportForm = ({
    // When this form is rendered standalone via the /reports route the
    // serviceId comes from the URL. When it's embedded (e.g. inside the
    // Lead Wizard's verification Sidebar) the parent passes it as a prop —
    // and the URL has no serviceId param. The prop wins; URL is the fallback.
    serviceId: serviceIdProp,
    serviceDetails: initialServiceDetails,
    // Optional: when fired from the Lead Wizard, the parent passes the
    // lead's id so the saved BureauBoxReport row gets linked to that lead
    // (via the model's creating hook on the backend).
    leadId,
    banner,
    fields = [],
    endpoint,
    consentReportType,
    submitLabel = 'Submit',
    successSummary = 'Done',
    errorSummary = 'Failed',
    outputComponent: OutputComponent,
    outputProps = {},
    // Optional password-protected PDF download. Provide a builder that turns
    // the report id into an endpoint path, e.g.
    //   pdfEndpoint: (id) => `/surepass/gstin-advanced/${id}/generate-pdf`
    // The backend streams the encrypted binary as an attachment (it is NOT
    // written to /storage — under tenancy that URL doesn't resolve), so we
    // pull it as a blob and save it client-side. When omitted, no download
    // button is rendered.
    pdfEndpoint,
    // Shown next to the download button so the user knows the password rule.
    pdfPasswordHint,
    confirmHeader = 'Confirm submission',
    confirmAcceptLabel = 'Yes, submit',
    customValidate,
    // Optional pre-fill bag from the host: { fieldName: value }. Used by the
    // Lead Wizard so a payslip-OCR-derived UAN auto-populates the UAN chip's
    // input form. Falls back to each field's `default` when a key is absent.
    initialValues,
    // Optional pre-loaded report payload. When the host (e.g. the Lead
    // Wizard's CIBIL drawer) has already fetched a report for this lead
    // earlier in the session OR rehydrated one from the bureau_box_reports
    // table, it can pass the same envelope shape this form sets after a
    // fresh fetch (`{success:true, data:{...}, report_id, pdf_*}`). The
    // form then renders in OUTPUT mode immediately — no Submit button,
    // no credit deduction. Critical for SurePass CIBIL where each fetch
    // costs real money (~₹120).
    initialReportData,
    // OTP consent gate (opt-in). When true, after the credit-confirm dialog
    // we send a free OTP to the borrower's mobile and require verification
    // BEFORE calling the (billable) endpoint. The verified single-use token
    // is forwarded to the endpoint as `consent_token`, which the backend
    // independently validates. Only forms hitting expensive bureau APIs
    // (e.g. CIBIL) enable this; the other ~50 report forms are unaffected.
    otpGate = false,
    // Name of the field holding the 10-digit mobile the OTP is sent to.
    otpMobileField = 'mobile',
}) => {
    const params = useParams();
    const serviceId = serviceIdProp ?? params.serviceId;
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState(() => buildInitial(fields, initialValues));
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(initialReportData || null);

    // #161: credits used to be fetched by a per-component useEffect with caching
    // switched off, so every one of the ~49 pages built on this form re-requested
    // them on every mount. useQuery dedupes concurrent callers and serves the
    // cache on revisit; the balance is invalidated explicitly after a report runs
    // (below), which is what makes caching correct rather than merely faster.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId, {
        // The host may already hold this (Lead Wizard passes it down); seed the
        // cache so an embedded form paints instantly instead of flashing a spinner.
        initialData: initialServiceDetails || undefined,
    });
    const invalidateReportService = useInvalidateReportService();

    // Preserve the old failure UX: show 'N/A' rather than an empty banner.
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || initialServiceDetails || null);

    // ── OTP consent gate state (only used when otpGate) ────────────────
    const [otpDialogVisible, setOtpDialogVisible] = useState(false);
    const [otpValue, setOtpValue] = useState('');
    const [otpSending, setOtpSending] = useState(false);
    const [otpVerifying, setOtpVerifying] = useState(false);
    const [otpError, setOtpError] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);
    const [otpLocked, setOtpLocked] = useState(false);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        fields.forEach((f) => {
            if (f.required === false) return;
            const v = formData[f.name];
            if (f.type === 'checkbox') {
                if (!v) next[f.name] = `${f.label} is required`;
                return;
            }
            const isEmpty = v === undefined || v === null || (typeof v === 'string' && !v.trim());
            if (isEmpty) {
                next[f.name] = `${f.label} is required`;
                return;
            }
            if (typeof v === 'string' && f.regex && !f.regex.test(v)) {
                next[f.name] = f.regexError || `Invalid ${f.label} format`;
                return;
            }
            if (typeof v === 'string' && f.minLength && v.trim().length < f.minLength) {
                next[f.name] = `${f.label} must be at least ${f.minLength} characters`;
            }
        });
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        if (customValidate) {
            const extra = customValidate(formData) || {};
            Object.assign(next, extra);
        }
        return next;
    };

    const submit = async (consentToken) => {
        setLoading(true);
        try {
            // Pass leadId as a 3rd arg to bodyMapper. Mappers that don't
            // need it can ignore it; mappers built for the Lead Wizard
            // append `lead_id: parseInt(lid, 10)` to the request body.
            const body = endpoint.bodyMapper
                ? endpoint.bodyMapper(formData, serviceId, leadId)
                : {
                    ...Object.fromEntries(fields.map((f) => [f.bodyKey || f.name, formData[f.name]])),
                    api_provider_service_id: parseInt(serviceId, 10),
                    ...(leadId ? { lead_id: parseInt(leadId, 10) } : {}),
                };
            // Always transmit the consent declaration (#112). It was previously
            // validated client-side only — it gated this button and never left
            // the browser, so nothing on the server recorded that the borrower
            // agreed. Backends that enforce consent read this; the rest ignore
            // an extra field, so it is safe to send everywhere. Sent as 'Y' to
            // match the convention the bureau endpoints already validate.
            //
            // Set outside the bodyMapper branch deliberately: mappers are
            // per-report and would each have to remember, which is exactly how
            // this was missed on 85 of 94 screens.
            if (body && typeof body === 'object' && body.consent === undefined) {
                body.consent = 'Y';
            }
            // Forward the server-issued single-use consent token when the OTP
            // gate is active. The backend rejects the call (403) without it.
            if (otpGate && consentToken) body.consent_token = consentToken;
            const method = (endpoint.method || 'POST').toUpperCase();
            const res = method === 'GET'
                ? await axiosInstance.get(endpoint.path, { params: body })
                : await axiosInstance.post(endpoint.path, body);
            if (!res.data.success) throw new Error(res.data.message || `${errorSummary}`);
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: successSummary, detail: 'Report ready below', life: 2000 });

            // Notify any host (e.g. the Lead Wizard's VerificationSidebar) that
            // a verification just succeeded so it can offer "Apply to Form".
            // Detail carries the raw response payload — listeners can pick out
            // whatever data shape they need (extracted_data / data / response).
            try {
                window.dispatchEvent(new CustomEvent('verification-report-saved', {
                    detail: {
                        serviceId: parseInt(serviceId, 10),
                        data: res.data?.data ?? null,
                        extracted_data: res.data?.data?.extracted_data ?? null,
                        response: res.data,
                    },
                }));
            } catch (e) {
                // Custom events not supported — degraded fallback is the
                // VerificationSidebar's manual /borrower-reports/latest poll.
            }

            // The report just spent credits, so the cached balance is now wrong.
            // Invalidating (rather than re-GETting here) updates every mounted
            // consumer of this service at once and is ordered strictly after the
            // deduction — the old inline refetch could race it and paint the
            // pre-charge number.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error(`${errorSummary}:`, error?.message || 'Unknown error');
            const status = error.response?.status;
            // Fallbacks only — the server's message must win.
            //
            // These are keyed on the status code alone, so they can only ever
            // guess. The backend already sends a sentence describing what
            // actually went wrong ("Data not found in Bureau", "not enabled on
            // your account", …); showing "Invalid input" over the top of it sent
            // people off re-checking a mobile number or PAN that was perfectly
            // fine. Every page built on this form was affected, not one.
            const map = {
                500: 'Internal server error. Please try again later.',
                503: 'The verification provider is unavailable right now. Please try again shortly — no credits were used.',
                422: 'Please check your inputs and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid input. Please verify and retry.',
                404: 'No record found.',
            };
            let detail = error.response?.data?.message || map[status] || error.message || errorSummary;
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            // 3.5s is not long enough to read a "contact support" sentence.
            toast.current?.show({ severity: 'error', summary: errorSummary, detail, life: detail.length > 60 ? 8000 : 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    // ── OTP consent gate handlers (only invoked when otpGate) ──────────
    const otpMobile = () => String(formData[otpMobileField] || '');

    const sendConsentOtp = async ({ isResend = false } = {}) => {
        setOtpSending(true);
        setOtpError('');
        try {
            const res = await axiosInstance.post('/consent-otp/send', { mobile: otpMobile() });
            if (!res.data.success) throw new Error(res.data.message || 'Failed to send OTP');
            setOtpValue('');
            setResendCooldown(30);
            if (!isResend) setOtpDialogVisible(true);
            toast.current?.show({
                severity: 'success',
                summary: isResend ? 'OTP resent' : 'OTP sent',
                detail: `A verification code was sent to ${otpMobile()}`,
                life: 2500,
            });
        } catch (error) {
            const detail = error.response?.data?.message || error.message || 'Failed to send OTP';
            if (error.response?.status === 429) {
                setOtpLocked(true);
                setResendCooldown(0);
                if (isResend) setOtpError(detail);
            }
            if (!isResend) submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'OTP send failed', detail, life: 4500 });
        } finally {
            setOtpSending(false);
        }
    };

    const verifyConsentOtp = async () => {
        if (!/^\d{4,8}$/.test(otpValue)) {
            setOtpError('Enter the complete OTP');
            return;
        }
        setOtpVerifying(true);
        setOtpError('');
        try {
            const res = await axiosInstance.post('/consent-otp/verify', { mobile: otpMobile(), otp: otpValue });
            if (!res.data.success) throw new Error(res.data.message || 'OTP verification failed');
            const consentToken = res.data.consent_token;
            if (!consentToken) throw new Error('Consent token missing from server response');
            setOtpDialogVisible(false);
            setOtpValue('');
            toast.current?.show({ severity: 'success', summary: 'Verified', detail: 'Consent confirmed — fetching report', life: 2000 });
            await submit(consentToken);
        } catch (error) {
            setOtpError(error.response?.data?.message || error.message || 'OTP verification failed');
            if (error.response?.status === 429) setOtpLocked(true);
        } finally {
            setOtpVerifying(false);
        }
    };

    const cancelOtp = () => {
        setOtpDialogVisible(false);
        setOtpValue('');
        setOtpError('');
        setOtpLocked(false);
        submittingRef.current = false;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        // Scope the confirm to a named group so it ONLY targets the
        // <ConfirmDialog group="report-form-confirm" /> rendered below.
        // Without `group`, both this form's ConfirmDialog AND any other
        // host's <ConfirmDialog /> (e.g. Lead Wizard's global one) would
        // catch the same event and render the dialog twice.
        confirmDialog({
            group: 'report-form-confirm',
            message: (
                <div className="text-sm">
                    {fields.filter((f) => f.showInConfirm !== false && f.type !== 'checkbox').map((f) => {
                        const v = formData[f.name];
                        if (v === '' || v === null || v === undefined) return null;
                        return (
                            <div key={f.name} className="mb-2">
                                <span className="text-600">{f.label}:</span> <span className="text-900 font-medium">{String(v)}</span>
                            </div>
                        );
                    })}
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: confirmHeader,
            icon: 'pi pi-question-circle text-primary pi-2x',
            // When the OTP gate is on, the confirm button sends the OTP and the
            // real submit() runs only after verification. Otherwise submit()
            // runs directly as before.
            acceptLabel: otpGate ? 'Send OTP & continue' : confirmAcceptLabel,
            rejectLabel: 'Cancel',
            accept: otpGate ? () => sendConsentOtp() : submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const handleAnother = () => {
        setReportData(null);
        // Preserve initialValues on reset too — if the host pre-filled
        // a UAN, "Run another check" shouldn't blank it out.
        setFormData(buildInitial(fields, initialValues));
        setErrors({});
    };

    // Pulls the password-protected PDF as a blob and saves it via an off-DOM
    // anchor. Mirrors EquifaxInputComponent.handleDownloadPDF — under tenancy
    // the static `/storage/...` URL doesn't resolve, so the backend streams
    // the bytes instead of handing us a link.
    const handleDownloadPDF = async () => {
        const reportId = reportData?.report_id || reportData?.id || reportData?.data?.report_id;
        if (!reportId) {
            toast.current?.show({ severity: 'warn', summary: 'PDF unavailable', detail: 'Report ID not available', life: 3000 });
            return;
        }

        try {
            const res = await axiosInstance.post(pdfEndpoint(reportId), {}, { responseType: 'blob' });

            let filename = `report-${reportId}.pdf`;
            const match = /filename="?([^"]+)"?/i.exec(res.headers?.['content-disposition'] || '');
            if (match?.[1]) filename = match[1];

            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.current?.show({ severity: 'success', summary: 'Download started', detail: filename, life: 2500 });
        } catch (error) {
            // Error bodies are JSON but arrive as a Blob because of responseType.
            let serverMessage = null;
            if (error.response?.data instanceof Blob) {
                try {
                    serverMessage = JSON.parse(await error.response.data.text())?.message;
                } catch (_) { /* ignore — fall through to status mapping */ }
            }

            console.error('PDF download failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const detail = serverMessage || (
                status === 404 ? 'Report not found. Please regenerate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                status === 500 ? 'Internal server error. Please try again later.' :
                'PDF download failed'
            );
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail, life: 3500 });
        }
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="report-form-confirm" />

            {otpGate && (
                <Dialog
                    header="Verify borrower's mobile"
                    visible={otpDialogVisible}
                    onHide={cancelOtp}
                    modal
                    draggable={false}
                    dismissableMask={false}
                    closable={!otpVerifying}
                    style={{ width: '90vw', maxWidth: '440px' }}
                >
                    <div className="flex flex-column align-items-center text-center">
                        <i className="pi pi-shield text-primary" style={{ fontSize: '2.5rem' }} aria-hidden="true" />
                        <p className="text-sm text-700 mt-3 mb-1">Enter the OTP sent to</p>
                        <p className="text-base font-semibold text-900 m-0 mb-4">
                            <i className="pi pi-phone text-500 mr-1" aria-hidden="true" />{otpMobile()}
                        </p>

                        <InputOtp
                            value={otpValue}
                            onChange={(e) => { setOtpValue((e.value ?? '').toString()); if (otpError) setOtpError(''); }}
                            length={6}
                            integerOnly
                            disabled={otpVerifying || otpLocked}
                        />

                        {otpError && (
                            <small className="p-error block mt-3" role="alert">
                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />{otpError}
                            </small>
                        )}

                        <div className="flex align-items-center gap-2 mt-4 w-full justify-content-center">
                            <Button
                                label="Verify & Fetch"
                                icon="pi pi-check"
                                onClick={verifyConsentOtp}
                                loading={otpVerifying}
                                disabled={otpVerifying || otpLocked || otpValue.length < 6}
                                size="small"
                            />
                        </div>

                        <div className="mt-3">
                            {otpLocked ? (
                                <span className="text-xs text-red-500">
                                    <i className="pi pi-lock mr-1" aria-hidden="true" />OTP limit reached — please try again later.
                                </span>
                            ) : resendCooldown > 0 ? (
                                <span className="text-xs text-500">Resend OTP in {resendCooldown}s</span>
                            ) : (
                                <Button
                                    label="Resend OTP"
                                    icon="pi pi-replay"
                                    link
                                    size="small"
                                    loading={otpSending}
                                    disabled={otpSending || otpVerifying}
                                    onClick={() => sendConsentOtp({ isResend: true })}
                                    className="p-0 text-xs"
                                />
                            )}
                        </div>
                    </div>
                </Dialog>
            )}
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon={banner.icon}
                        title={banner.title}
                        subtitle={banner.subtitle}
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    {fields.map((f) => renderField(f, formData, errors, setFormData, setErrors, loading))}
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType={consentReportType}
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label={submitLabel}
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Submit another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <OutputComponent
                                reportData={reportData}
                                {...(pdfEndpoint ? { onDownloadPDF: handleDownloadPDF, pdfPasswordHint } : {})}
                                {...outputProps}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SimpleReportForm;
